import ApplicationController from './application_controller';
import Modal from 'bootstrap/js/dist/modal';

export default class extends ApplicationController {
  static targets = ['modalContainer'];
  static values = {
    errorMessage: String,
  };

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || null;
    this.handleModalHidden = this.handleModalHidden.bind(this);
    this.modalInstance = null;
    this.modalElement = null;
    this.pendingModalHtml = null;
    this.activeTrigger = null;
  }

  disconnect() {
    this.teardownModal();
    if (this.hasModalContainerTarget) {
      this.modalContainerTarget.innerHTML = '';
    }
  }

  openModal(event) {
    event.preventDefault();
    this.activeTrigger = event.currentTarget;
    const url = event.currentTarget.getAttribute('href');
    if (!url) {
      return;
    }

    this.fetchModal(url);
  }

  closeModal(event) {
    if (event) {
      event.preventDefault();
    }

    if (this.modalInstance) {
      this.modalInstance.hide();
    }
  }

  submitForm(event) {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);
    const submitButton = form.querySelector('[type="submit"]');

    if (submitButton) {
      submitButton.disabled = true;
    }

    fetch(form.action, {
      method: form.method.toUpperCase(),
      headers: this.requestHeaders(),
      body: formData,
      credentials: 'same-origin',
    })
      .then(async (response) => {
        if (submitButton) {
          submitButton.disabled = false;
        }

        if (response.ok) {
          if (response.redirected) {
            window.location.href = response.url;
            return;
          }

          this.closeModal();
          window.location.reload();
          return;
        }

        if (response.status === 422) {
          this.pendingModalHtml = await response.text();
          this.closeModal();
          return;
        }

        throw new Error(`Unexpected response status: ${response.status}`);
      })
      .catch((error) => {
        console.error('Calendar events form submission failed', error);
        if (submitButton) {
          submitButton.disabled = false;
        }
        alert(this.errorMessageValue || 'We could not process your request. Please try again.');
      });
  }

  fetchModal(url) {
    fetch(url, {
      headers: this.requestHeaders(),
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unexpected response status: ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        if (!this.hasModalContainerTarget) {
          return;
        }
        this.modalContainerTarget.innerHTML = html;
        this.initializeModal();
      })
      .catch((error) => {
        console.error('Calendar events modal failed to load', error);
        alert(this.errorMessageValue || 'We could not process your request. Please try again.');
      });
  }

  initializeModal() {
    this.teardownModal();

    if (!this.hasModalContainerTarget) {
      return;
    }

    const modalElement = this.modalContainerTarget.querySelector('.modal');
    if (!modalElement) {
      return;
    }

    this.modalElement = modalElement;
    this.modalElement.addEventListener('hidden.bs.modal', this.handleModalHidden);

    this.modalInstance = new Modal(modalElement);
    this.modalInstance.show();

    // Initialize single date picker for event date
    this.initializeDatePicker();
  }

  initializeDatePicker() {
    const $ = window.jQuery || window.$;
    const moment = window.moment;

    if ($ && moment && this.modalElement) {
      const dateInput = $(this.modalElement).find('.date-range');

      if (dateInput.length) {
        // Set Spanish locale for moment
        moment.locale('es');

        dateInput.daterangepicker({
          singleDatePicker: true,
          autoUpdateInput: true,
          autoApply: true,
          locale: {
            format: 'DD/MM/YYYY',
            separator: ' - ',
            applyLabel: 'Aplicar',
            cancelLabel: 'Cancelar',
            daysOfWeek: moment.weekdaysMin(),
            monthNames: moment.monthsShort(),
            firstDay: moment.localeData().firstDayOfWeek()
          }
        });
      }
    }
  }

  teardownModal() {
    if (this.modalElement) {
      this.modalElement.removeEventListener('hidden.bs.modal', this.handleModalHidden);
      this.modalElement = null;
    }

    if (this.modalInstance) {
      this.modalInstance.dispose();
      this.modalInstance = null;
    }
  }

  handleModalHidden() {
    this.teardownModal();

    if (this.pendingModalHtml) {
      if (this.hasModalContainerTarget) {
        this.modalContainerTarget.innerHTML = this.pendingModalHtml;
      }
      this.pendingModalHtml = null;
      this.initializeModal();
      return;
    }

    if (this.hasModalContainerTarget) {
      this.modalContainerTarget.innerHTML = '';
    }

    if (this.activeTrigger && typeof this.activeTrigger.focus === 'function') {
      this.activeTrigger.focus();
    }

    this.activeTrigger = null;
  }

  requestHeaders() {
    const headers = {
      'X-Requested-With': 'XMLHttpRequest',
    };

    if (this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }

    return headers;
  }
}
